import React, { createContext, useCallback, useState } from "react";

import { useMutation, useQuery } from "@tanstack/react-query";
import ItemJobService from "Services/item.job.service";
import { ItemJobKeys } from "Util/queries";

import useVideoContext from "../components/VideoSurvey/hooks/useVideoContext/useVideoContext";
import { Destination } from "../models/destination";
import { Material } from "../models/material";
import { Sales as Job, QuotedItem, RoomJob } from "../models/sales";
import { ServiceBooking } from "../models/service.booking";
import DestinationService from "../services/destination.service";
import MaterialsService from "../services/materials.service";
import QuoteService from "../services/quote.service";
import { handleGetConvertValue, Metric } from "../util/converts";
import { DESTINATION, ItemJobSubStatus, Status } from "../util/enums";
import { getMeasurement, getWeightMeasurement } from "../util/getMeasurement";
import { useAppState } from "./StateContext";

const UPDATE_MESSAGE_TIMEOUT = 3 * 1000;

type SumItemsAndMaterialsProps = {
  room?: RoomJob;
  type: "volume" | "quantity";
};

type CalculateValuesByRoomProps = {
  quantity: number;
  volume: number;
  weight: number;
};

type VideoSurveyInventoryContextProps = {
  updatingQuoteInventory: boolean;
  inventoryUpdated: boolean;
  inventory: RoomJob[];
  destinations: Destination[];
  setInventory: React.Dispatch<React.SetStateAction<RoomJob[]>>;
  selectedRoomName: string;
  setSelectedRoomName: React.Dispatch<React.SetStateAction<string>>;
  job?: Job;
  setJob: React.Dispatch<React.SetStateAction<Job | undefined>>;
  updateEnabled: boolean;
  setUpdateEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  isInventoryCollapsed: boolean;
  setIsInventoryCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  updatedRoom: (olderRoomName: string, newRoomName: string, indexUpdate: number) => void;
  deleteRoom: (roomToDelete: RoomJob) => void;
  handleUpdateItem: (roomName: string, itemIndex: number, item: QuotedItem) => void;
  handleDeleteItem: (roomName: string, itemIndex: number) => void;
  handleUpdateMaterial: (roomName: string, materialIndex: number, material: Material) => void;
  handleDeleteMaterial: (roomName: string, materialIndex: number) => void;
  handleAddPicturesToRoom: (roomName: string, pictures: (string | null)[]) => void;
  handleDeletePicturesFromRoom: (roomName: string, uri?: string | undefined) => void;
  calculateValuesByRoom: () => CalculateValuesByRoomProps;
  updateQuoteInventory: () => void;
  totalVolume: (room: RoomJob | undefined) => string | number;
  totalWeight: (room: RoomJob | undefined) => string | number;
  sumItemsAndMaterials: ({ type, room }: SumItemsAndMaterialsProps) => number;
  packingMaterials?: ServiceBooking[];
};

export const VideoSurveyInventoryContext = createContext({} as VideoSurveyInventoryContextProps);

const VideoSurveyInventoryContextProvider: React.FC = ({ children }) => {
  const { currentUser } = useAppState();

  const measurement = getMeasurement();
  const measurementWeight = getWeightMeasurement();

  const { task } = useVideoContext();

  const [job, setJob] = useState<Job>();
  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [inventory, setInventory] = useState<RoomJob[]>([]);
  const [selectedRoomName, setSelectedRoomName] = useState<string>("");
  const [isInventoryCollapsed, setIsInventoryCollapsed] = useState<boolean>(false);

  const [updateEnabled, setUpdateEnabled] = useState<boolean>(false);
  const [inventoryUpdated, setInventoryUpdated] = useState<boolean>(false);

  useQuery({
    queryKey: ItemJobKeys.getInventory([task?.jobId]),
    queryFn: async () => {
      const response = await ItemJobService.getInventory({
        type: [ItemJobSubStatus.QUOTED],
        jobId: task?.jobId,
        isNewVersion: true,
        companyId: currentUser?.companyId,
      });

      if (response?.data && response?.data?.quotedInventory) {
        setInventory(response?.data?.quotedInventory);
      }

      return [];
    },
    enabled: !!task?.jobId,
  });

  const { refetch: getJob } = useQuery(
    ["job.get", task],
    () =>
      QuoteService.getById(Number(task?.jobId), {
        isSalesApp: true,
        isNewVersion: true,
        include: JSON.stringify([{ association: "agent" }]),
      }),
    {
      enabled: !!task?.jobId,
      refetchOnWindowFocus: false,
      cacheTime: 0,
      onSuccess: (response) => {
        if (response && response.data) {
          setJob(response.data);
        }
      },
    }
  );

  const { mutate: updateQuoteInventory, isLoading: updatingQuoteInventory } = useMutation(
    ["quote.inventory.update"],
    () => QuoteService.updateInventory(Number(task?.jobId), inventory),
    {
      onSuccess: () => {
        setInventoryUpdated(true);

        setTimeout(() => {
          setUpdateEnabled(false);
          setInventoryUpdated(false);
        }, UPDATE_MESSAGE_TIMEOUT);
      },
    }
  );

  useQuery(
    ["destination.list"],
    () =>
      DestinationService.getAll({
        status: Status.ACTIVE,
        companyId: currentUser?.companyId,
      }),
    {
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) {
          setDestinations(response.data);
        }
      },
    }
  );

  const { data: packingMaterials } = useQuery(
    ["materials.getAll"],
    async () => {
      const response = await MaterialsService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        orderBy: JSON.stringify(["name"]),
      });

      if (response && response?.data instanceof Array) {
        return response?.data;
      }

      return [];
    },
    {
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
    }
  );

  const cloneInventory = (): RoomJob[] => {
    let clonedInventory: RoomJob[] = [];

    try {
      clonedInventory = JSON.parse(JSON.stringify(inventory));
    } catch {
      clonedInventory = [];
    }

    return clonedInventory;
  };

  const updatedRoom = (olderRoomName: string, newRoomName: string, indexUpdate: number) => {
    const clonedRoom = cloneInventory();

    if (clonedRoom.length > 0) {
      const roomToEdit = clonedRoom[indexUpdate];

      if (roomToEdit.name === olderRoomName && roomToEdit.name !== newRoomName) {
        clonedRoom[indexUpdate].name = newRoomName;

        setInventory(clonedRoom);
      }
    }
  };

  const deleteRoom = (roomToDelete: RoomJob) => {
    const clonedRoom = cloneInventory();

    if (clonedRoom.length > 0) {
      const indexToDelete = clonedRoom.findIndex(
        (room: RoomJob) => room.name.toLowerCase() === roomToDelete.name.toLowerCase()
      );

      if (indexToDelete > -1) {
        clonedRoom.splice(indexToDelete, 1);

        if (roomToDelete?.name && roomToDelete?.name === selectedRoomName) setSelectedRoomName("");

        setInventory(clonedRoom);
        if (!updateEnabled) setUpdateEnabled(true);
      }
    }
  };

  const handleUpdateItem = (roomName: string, itemIndex: number, item: QuotedItem) => {
    const clonedRoom = cloneInventory();

    const roomToUpdate = clonedRoom.find((room) => room.name === roomName);

    if (roomToUpdate && roomToUpdate.itemsJob) {
      roomToUpdate.itemsJob[itemIndex] = {
        ...roomToUpdate.itemsJob[itemIndex],
        ...item,
      };
    }

    setInventory([...clonedRoom]);
    if (!updateEnabled) setUpdateEnabled(true);
  };

  const handleDeleteItem = (roomName: string, itemIndex: number) => {
    const clonedRoom = cloneInventory();

    const roomToUpdate = clonedRoom.find((room) => room.name === roomName);

    if (roomToUpdate && roomToUpdate.itemsJob) {
      const itemsJobDeleted = roomToUpdate?.itemsJobDeleted || [];

      itemsJobDeleted.push(roomToUpdate?.itemsJob[itemIndex]);

      roomToUpdate.itemsJob?.splice(itemIndex, 1);

      roomToUpdate.itemsJobDeleted = itemsJobDeleted;

      setInventory([...clonedRoom]);
      if (!updateEnabled) setUpdateEnabled(true);
    }
  };

  const handleUpdateMaterial = (roomName: string, materialIndex: number, material: Material) => {
    const clonedRoom = cloneInventory();

    const roomToUpdate = clonedRoom.find((room) => room.name === roomName);

    if (roomToUpdate) roomToUpdate.materials[materialIndex] = material;

    setInventory([...clonedRoom]);
    if (!updateEnabled) setUpdateEnabled(true);
  };

  const handleDeleteMaterial = (roomName: string, materialIndex: number) => {
    const clonedRoom = cloneInventory();

    const roomToUpdate = clonedRoom.find((room) => room.name === roomName);

    if (roomToUpdate && roomToUpdate.materials) {
      const materialsDeleted = roomToUpdate?.materialsDeleted || [];

      materialsDeleted.push(roomToUpdate.materials[materialIndex]);

      roomToUpdate.materials.splice(materialIndex, 1);

      roomToUpdate.materialsDeleted = materialsDeleted;

      setInventory([...clonedRoom]);
      if (!updateEnabled) setUpdateEnabled(true);
    }
  };

  const handleAddPicturesToRoom = (roomName: string, pictures: (string | null)[]) => {
    const clonedRoom = cloneInventory();
    const roomToUpdate = clonedRoom.find((room) => room.name === roomName);

    if (roomToUpdate) {
      roomToUpdate.pictures = [...pictures, ...(roomToUpdate.pictures || [])];
    }

    setInventory([...clonedRoom]);
    if (!updateEnabled) setUpdateEnabled(true);
  };

  const handleDeletePicturesFromRoom = (roomName: string, uri?: string) => {
    if (uri) {
      const clonedRoom = cloneInventory();

      const roomToUpdate = clonedRoom.find((room) => room.name === roomName);

      if (roomToUpdate) {
        const pictures = roomToUpdate.pictures || [];
        const indexPicture = pictures.indexOf(uri);

        if (indexPicture > -1) {
          delete pictures[indexPicture];

          roomToUpdate.pictures = pictures.filter(Boolean);

          setInventory([...clonedRoom]);
          if (!updateEnabled) setUpdateEnabled(true);

          return;
        }

        const roomPictures = roomToUpdate.roomPictures || [];
        const roomPicturesDeleted = roomToUpdate.roomPicturesDeleted || [];

        roomPictures?.forEach((roomPicture, index) => {
          if (roomPicture.mediaLink === uri) {
            roomPicturesDeleted.push(roomPicture);

            delete roomPictures[index];
          }
        });

        roomToUpdate.pictures = pictures.filter(Boolean);
        roomToUpdate.roomPictures = roomPictures.filter(Boolean);
        roomToUpdate.roomPicturesDeleted = roomPicturesDeleted;

        setInventory([...clonedRoom]);
        if (!updateEnabled) setUpdateEnabled(true);
      }
    }
  };

  const calculateValuesByRoom = useCallback((): CalculateValuesByRoomProps => {
    let quantity = 0;
    let volume = 0;
    let weight = 0;

    inventory?.forEach((room: RoomJob) => {
      if (room?.materials && room?.materials?.length > 0) {
        room.materials.forEach((material) => {
          if (material?.serviceBooking?.isItem) {
            quantity += Number(material?.quantity);
            volume += Number(material?.volume) * Number(material?.quantity);
            weight += Number(material?.weight) * Number(material?.quantity);
          }
        });
      }

      if (room?.itemsJob && room?.itemsJob?.length > 0) {
        room.itemsJob.forEach((item: QuotedItem) => {
          quantity += Number(item?.itemJob?.quantity);
          volume += Number(item?.itemJob?.volume) * Number(item?.itemJob?.quantity);
          weight += Number(item?.itemJob?.weight) * Number(item?.itemJob?.quantity);
        });
      }
    });

    const convertedWeight = Number(
      handleGetConvertValue(
        Number(weight),
        measurementWeight?.code,
        measurementWeight?.decimalPlaces
      )
    );

    const weightFixed = convertedWeight.toFixed(2);

    const convertedVolume = Number(
      handleGetConvertValue(Number(volume), measurement?.code, measurement?.decimalPlaces)
    );

    const volumeFixed = measurement?.code?.includes("ft")
      ? convertedVolume
      : convertedVolume.toFixed(2);

    return {
      quantity,
      volume: Number(volumeFixed),
      weight: Number(weightFixed),
    };
  }, [inventory, currentUser]);

  const sumItemsAndMaterials = ({ type, room }: SumItemsAndMaterialsProps): number => {
    let materialSum = 0;

    const itemsSum = room?.itemsJob
      ? room.itemsJob
          .filter(
            (item) =>
              item?.itemJob?.destination?.name?.toLowerCase() !==
              DESTINATION.NOT_GOING?.toLowerCase()
          )
          .reduce((total: number, item: QuotedItem) => total + Number(item?.itemJob[type]), 0)
      : 0;

    room?.materials?.forEach((material) => {
      if (
        material?.serviceBooking?.isItem &&
        material?.destination?.name?.toLowerCase() !== DESTINATION.NOT_GOING?.toLowerCase()
      ) {
        materialSum += material.quantity;
      }
    });

    return Number(itemsSum) + Number(materialSum);
  };

  const totalVolume = (room: RoomJob | undefined) => {
    let itemsTotal = 0;
    let materialsTotal = 0;

    if (room?.itemsJob && room?.itemsJob.length > 0)
      itemsTotal = room.itemsJob
        ?.filter(
          (item) =>
            item?.itemJob?.destination?.name?.toLowerCase() !== DESTINATION.NOT_GOING?.toLowerCase()
        )
        .reduce(
          (total: number, item: QuotedItem) =>
            total +
            Number(
              handleGetConvertValue(
                Number(item?.itemJob?.volume),
                measurement?.code,
                measurement?.decimalPlaces
              )
            ) *
              Number(item?.itemJob?.quantity),
          0
        );

    if (room?.materials && room.materials.length > 0)
      materialsTotal = room.materials
        .filter(
          (material) =>
            material?.destination?.name?.toLowerCase() !== DESTINATION.NOT_GOING?.toLowerCase()
        )
        .reduce(
          (total: number, material: Material) =>
            total +
            (material?.serviceBooking?.isItem
              ? Number(
                  handleGetConvertValue(
                    Number(material.volume),
                    measurement?.code,
                    measurement?.decimalPlaces
                  )
                ) * Number(material.quantity)
              : 0),
          0
        );

    return measurement?.code?.includes("ft")
      ? itemsTotal + materialsTotal
      : (itemsTotal + materialsTotal).toFixed(2);
  };

  const totalWeight = (room: RoomJob | undefined) => {
    let itemsTotal = 0;
    let materialsTotal = 0;

    if (room?.itemsJob && room?.itemsJob.length > 0) {
      itemsTotal = room?.itemsJob
        ?.filter(
          (item) =>
            item?.itemJob?.destination?.name?.toLowerCase() !== DESTINATION.NOT_GOING?.toLowerCase()
        )
        ?.reduce(
          (total: number, item: QuotedItem) =>
            total +
            Number(
              Number(
                handleGetConvertValue(
                  Number(item?.itemJob?.weight),
                  measurementWeight?.code || Metric.kg,
                  measurementWeight?.decimalPlaces
                )
              ) * Number(item?.itemJob?.quantity)
            ),
          0
        );
    }

    if (room?.materials && room?.materials?.length > 0) {
      materialsTotal = room?.materials
        ?.filter(
          (material) =>
            material?.destination?.name?.toLowerCase() !== DESTINATION.NOT_GOING?.toLowerCase()
        )
        ?.reduce(
          (total: number, material: Material) =>
            total +
            (material?.serviceBooking?.isItem
              ? Number(
                  handleGetConvertValue(
                    Number(material?.weight || 0),
                    measurementWeight?.code || Metric.kg,
                    measurementWeight?.decimalPlaces
                  )
                ) * Number(material.quantity)
              : 0),
          0
        );
    }

    return Number(itemsTotal + materialsTotal);
  };

  return (
    <VideoSurveyInventoryContext.Provider
      value={{
        updatingQuoteInventory,
        inventoryUpdated,
        inventory,
        destinations,
        setInventory,
        selectedRoomName,
        setSelectedRoomName,
        updateEnabled,
        setUpdateEnabled,
        job,
        setJob,
        isInventoryCollapsed,
        setIsInventoryCollapsed,
        updatedRoom,
        deleteRoom,
        handleUpdateItem,
        handleDeleteItem,
        handleUpdateMaterial,
        handleDeleteMaterial,
        handleAddPicturesToRoom,
        handleDeletePicturesFromRoom,
        calculateValuesByRoom,
        updateQuoteInventory,
        sumItemsAndMaterials,
        totalVolume,
        totalWeight,
        packingMaterials,
      }}
    >
      {children}
    </VideoSurveyInventoryContext.Provider>
  );
};

export default VideoSurveyInventoryContextProvider;
